import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  User, Building, Mail, Phone, MapPin, Calendar, CheckCircle,
  XCircle, AlertCircle, Clock, Users, BookOpen, Target,
  ArrowRight, RefreshCw, Award, Shield
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverAssignment = () => {
  const navigate = useNavigate();
  const [principal, setPrincipal] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      const [principalRes, statusRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/principal`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/observer/assignment-status`, { params: { token } })
      ]);
      
      setPrincipal(principalRes.data);
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading assignment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusIcon = (completed) => {
    return completed ? (
      <CheckCircle className="w-5 h-5 text-green-500" />
    ) : (
      <XCircle className="w-5 h-5 text-gray-300" />
    );
  };

  const calculateReadiness = () => {
    if (!status) return 0;
    let steps = 0;
    if (status.nda_signed) steps++;
    if (status.training_completed) steps++;
    if (status.availability_set) steps++;
    if (status.principal_assigned) steps++;
    if (status.assigned_children > 0) steps++;
    return (steps / 5) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Building className="w-7 h-7 text-violet-600" />
                My Assignment
              </h1>
              <p className="text-gray-600">View your principal assignment and onboarding status</p>
            </div>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Readiness Card */}
          {status && (
            <Card className={`mb-6 border-l-4 ${status.ready_for_sessions ? 'border-l-green-500' : 'border-l-yellow-500'}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      status.ready_for_sessions ? 'bg-green-100' : 'bg-yellow-100'
                    }`}>
                      {status.ready_for_sessions ? (
                        <Award className="w-6 h-6 text-green-600" />
                      ) : (
                        <Target className="w-6 h-6 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {status.ready_for_sessions ? 'Ready for Sessions!' : 'Onboarding In Progress'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {status.ready_for_sessions 
                          ? 'You are fully set up and can start conducting sessions' 
                          : 'Complete the remaining steps to start observing'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-violet-600">{calculateReadiness().toFixed(0)}%</p>
                    <p className="text-sm text-gray-500">Complete</p>
                  </div>
                </div>
                <Progress value={calculateReadiness()} className="h-2" />
              </CardContent>
            </Card>
          )}

          {/* Principal Assignment */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-violet-600" />
                Assigned Principal
              </h2>
              
              {principal?.assigned ? (
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {principal.principal.name?.charAt(0) || 'P'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">{principal.principal.name}</h3>
                    <p className="text-violet-600 font-medium">{principal.principal.school_name}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">{principal.principal.email}</span>
                      </div>
                      {principal.principal.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span className="text-sm">{principal.principal.phone}</span>
                        </div>
                      )}
                      {(principal.principal.city || principal.principal.state) && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">
                            {[principal.principal.city, principal.principal.state].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">Assigned: {formatDate(principal.principal.assigned_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">No Principal Assigned Yet</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Complete your onboarding steps and a principal will be assigned to you.
                  </p>
                  <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={() => navigate('/observer/training')}>
                      <BookOpen className="w-4 h-4 mr-2" />
                      Complete Training
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Onboarding Checklist */}
          {status && (
            <Card className="mb-6">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-violet-600" />
                  Onboarding Checklist
                </h2>
                
                <div className="space-y-4">
                  {/* NDA */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    status.nda_signed ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status.nda_signed)}
                      <div>
                        <p className="font-medium text-gray-900">Sign NDA & Agreements</p>
                        <p className="text-sm text-gray-500">Complete confidentiality agreement</p>
                      </div>
                    </div>
                    {!status.nda_signed && (
                      <Button size="sm" onClick={() => navigate('/observer/nda')}>
                        Complete <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>

                  {/* Training */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    status.training_completed ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status.training_completed)}
                      <div>
                        <p className="font-medium text-gray-900">Complete Training</p>
                        <p className="text-sm text-gray-500">
                          {status.training_modules_completed}/6 modules completed
                        </p>
                      </div>
                    </div>
                    {!status.training_completed && (
                      <Button size="sm" onClick={() => navigate('/observer/training')}>
                        Continue <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>

                  {/* Availability */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    status.availability_set ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status.availability_set)}
                      <div>
                        <p className="font-medium text-gray-900">Set Availability</p>
                        <p className="text-sm text-gray-500">Configure your schedule preferences</p>
                      </div>
                    </div>
                    {!status.availability_set && (
                      <Button size="sm" onClick={() => navigate('/observer/availability')}>
                        Set Up <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>

                  {/* Principal Assignment */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    status.principal_assigned ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status.principal_assigned)}
                      <div>
                        <p className="font-medium text-gray-900">Principal Assignment</p>
                        <p className="text-sm text-gray-500">
                          {status.principal_assigned ? 'Assigned to a principal' : 'Waiting for assignment'}
                        </p>
                      </div>
                    </div>
                    {!status.principal_assigned && (
                      <span className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-4 h-4" /> Pending
                      </span>
                    )}
                  </div>

                  {/* Children Assignment */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    status.assigned_children > 0 ? 'bg-green-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-3">
                      {getStatusIcon(status.assigned_children > 0)}
                      <div>
                        <p className="font-medium text-gray-900">Children Assignment</p>
                        <p className="text-sm text-gray-500">
                          {status.assigned_children > 0 
                            ? `${status.assigned_children} children assigned` 
                            : 'Waiting for children assignment'}
                        </p>
                      </div>
                    </div>
                    {status.assigned_children > 0 && (
                      <Button size="sm" variant="outline" onClick={() => navigate('/observer/dashboard')}>
                        <Users className="w-4 h-4 mr-1" /> View
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status Summary */}
          {status && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-violet-600">{status.training_modules_completed}/6</p>
                  <p className="text-sm text-gray-500">Training</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{status.assigned_children}</p>
                  <p className="text-sm text-gray-500">Children</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-bold ${status.status === 'active' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {status.status === 'active' ? 'Active' : 'Pending'}
                  </p>
                  <p className="text-sm text-gray-500">Status</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className={`text-2xl font-bold ${status.is_accepting_new_children ? 'text-green-600' : 'text-red-600'}`}>
                    {status.is_accepting_new_children ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-500">Accepting</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverAssignment;
